import { useState, useEffect, useMemo, useCallback } from 'react';
import { ScimEntityService } from '../services/ScimEntityService';
import { getAttributeDisplayNameForPropertyPath, type EntityEntitlementForTargetResponse } from '../types/EntityEntitlements';
import { getEntityValueFromARef } from '../types';
import { usePerEntityTableEntitlement } from './usePerEntityTableEntitlement';
import { filterRequestedAttributesByEntitlements, getDefaultRequestedAttributesForEntityType } from '../utils/tableAttributesUtils';

export interface PaginatedArrayRefViewerState {
    field: string;
    mode: 'view' | 'remove';
}

export interface UsePaginatedArrayRefViewerParams {
    open: boolean;
    entityType: string;
    entityId: string | null | undefined;
    formData: Record<string, unknown>;
    isSet: boolean;
    targetEntitlement: EntityEntitlementForTargetResponse | null;
    entityService: ScimEntityService;
}

export function usePaginatedArrayRefViewer({
    open,
    entityType,
    entityId,
    formData,
    isSet,
    targetEntitlement,
    entityService,
}: UsePaginatedArrayRefViewerParams) {
    const [paginatedArrayRefViewer, setPaginatedArrayRefViewer] = useState<PaginatedArrayRefViewerState | null>(null);
    const [viewerEntities, setViewerEntities] = useState<any[]>([]);
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewerError, setViewerError] = useState<string | null>(null);
    const [viewerPage, setViewerPage] = useState(1);
    const [viewerTotalPages, setViewerTotalPages] = useState(1);
    const [viewerTotalResults, setViewerTotalResults] = useState(0);
    const [viewerSelectedIds, setViewerSelectedIds] = useState<string[]>([]);
    const [viewerSelectedEntities, setViewerSelectedEntities] = useState<any[]>([]);

    useEffect(() => {
        if (!open) {
            setPaginatedArrayRefViewer(null);
            setViewerEntities([]);
            setViewerError(null);
            setViewerPage(1);
            setViewerTotalPages(1);
            setViewerTotalResults(0);
            setViewerSelectedIds([]);
            setViewerSelectedEntities([]);
            return;
        }
        if (paginatedArrayRefViewer) {
            setViewerPage(1);
            setViewerSelectedIds([]);
            setViewerSelectedEntities([]);
        }
    }, [open, paginatedArrayRefViewer]);

    const viewerEntityType = useMemo(() => {
        if (!paginatedArrayRefViewer?.field) return undefined;
        if (paginatedArrayRefViewer.field === 'ManualSetMember' && isSet) {
            const scope = (getEntityValueFromARef('FilterScope', formData ?? {}) ?? '').toString().trim();
            return scope || 'employee';
        }
        return 'employee';
    }, [paginatedArrayRefViewer?.field, isSet, formData]);

    const { allowedTableAttributeIds: viewerAllowedTableAttributeIds } = usePerEntityTableEntitlement({
        entityType: viewerEntityType ?? undefined,
        authResolved: true,
    });

    const viewerEffectiveAttributesForDisplay = useMemo(() => {
        if (viewerEntityType == null || viewerAllowedTableAttributeIds === null) return [];
        const normalized = (viewerEntityType ?? 'employee').toLowerCase().replace(/\s+/g, '');
        const requested = getDefaultRequestedAttributesForEntityType(normalized);
        return filterRequestedAttributesByEntitlements(requested, viewerAllowedTableAttributeIds ?? undefined);
    }, [viewerEntityType, viewerAllowedTableAttributeIds]);

    const viewerExtraColumnAttributes = useMemo(() => {
        const core = ['Id', 'DisplayName', 'EntityType'];
        const coreLower = new Set(core.map((c) => c.toLowerCase()));
        return viewerEffectiveAttributesForDisplay.filter((a) => a && !coreLower.has((a ?? '').toLowerCase()));
    }, [viewerEffectiveAttributesForDisplay]);

    useEffect(() => {
        if (!paginatedArrayRefViewer?.field || !entityType || !entityId) return;
        if (viewerAllowedTableAttributeIds === null) {
            setViewerLoading(true);
            return;
        }
        const propertyDisplayName = getAttributeDisplayNameForPropertyPath(targetEntitlement ?? null, paginatedArrayRefViewer.field);
        const normalizedViewerType = (viewerEntityType ?? 'employee').toLowerCase().replace(/\s+/g, '');
        const requestedAttributes = getDefaultRequestedAttributesForEntityType(normalizedViewerType);
        const effectiveAttributes = filterRequestedAttributesByEntitlements(requestedAttributes, viewerAllowedTableAttributeIds ?? undefined);
        setViewerLoading(true);
        setViewerError(null);
        entityService
            .getEntityPropertyPage(entityType, entityId, propertyDisplayName, viewerPage, 25, undefined, effectiveAttributes)
            .then((res) => {
                setViewerEntities(res.data ?? []);
                setViewerTotalResults(res.totalResults ?? 0);
                setViewerTotalPages(Math.max(1, res.totalPages ?? 1));
            })
            .catch((err: { message?: string }) => {
                setViewerError(err?.message ?? 'Failed to load');
                setViewerEntities([]);
                setViewerTotalResults(0);
                setViewerTotalPages(1);
            })
            .finally(() => setViewerLoading(false));
    }, [
        paginatedArrayRefViewer?.field,
        entityType,
        entityId,
        viewerPage,
        targetEntitlement,
        entityService,
        viewerEntityType,
        viewerAllowedTableAttributeIds,
    ]);

    const handleViewerEntitySelect = useCallback(
        (id: string, isSelected: boolean) => {
            if (isSelected) {
                setViewerSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
                const entity = viewerEntities.find((e) => (e.Id ?? e.id) === id);
                if (entity) {
                    setViewerSelectedEntities((prev) =>
                        prev.some((e) => (e.Id ?? e.id) === id) ? prev : [...prev, entity]
                    );
                }
            } else {
                setViewerSelectedIds((prev) => prev.filter((x) => x !== id));
                setViewerSelectedEntities((prev) => prev.filter((e) => (e.Id ?? e.id) !== id));
            }
        },
        [viewerEntities]
    );

    const handleViewerSelectAll = useCallback(() => {
        const visibleIds = viewerEntities.map((e) => e.Id ?? e.id);
        if (viewerSelectedIds.length === viewerEntities.length) {
            setViewerSelectedIds((prev) => prev.filter((x) => !visibleIds.includes(x)));
            setViewerSelectedEntities((prev) => prev.filter((e) => !visibleIds.includes(e.Id ?? e.id)));
        } else {
            setViewerSelectedIds((prev) => {
                const next = [...prev];
                visibleIds.forEach((id) => {
                    if (!next.includes(id)) next.push(id);
                });
                return next;
            });
            setViewerSelectedEntities((prev) => {
                const next = [...prev];
                viewerEntities.forEach((entity) => {
                    const id = entity.Id ?? entity.id;
                    if (!next.some((e) => (e.Id ?? e.id) === id)) next.push(entity);
                });
                return next;
            });
        }
    }, [viewerEntities, viewerSelectedIds.length]);

    const closeViewer = useCallback(() => setPaginatedArrayRefViewer(null), []);

    return {
        paginatedArrayRefViewer,
        setPaginatedArrayRefViewer,
        viewerEntities,
        viewerLoading,
        viewerError,
        viewerPage,
        setViewerPage,
        viewerTotalPages,
        viewerTotalResults,
        viewerSelectedIds,
        setViewerSelectedIds,
        viewerSelectedEntities,
        setViewerSelectedEntities,
        viewerExtraColumnAttributes,
        handleViewerEntitySelect,
        handleViewerSelectAll,
        closeViewer,
    };
}
